import logoUrl from "@/assets/bigY_logo.png";
import { AppFooter } from "@/components/layout/app-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { ReactNode } from "react";

type LoginLayoutProps = {
  readonly children: ReactNode;
};

type SessionUnavailableCardProps = {
  readonly onRetry: () => void;
};

export function LoginLayout({ children }: LoginLayoutProps) {
  return (
    <div className="bg-login-bg flex min-h-screen flex-col">
      <header className="border-login-border-soft flex h-12 items-center border-b bg-white px-5">
        <div className="flex flex-col leading-none">
          <span className="text-trux-navy text-badge tracking-brand-1 font-extrabold">
            TRU<span className="text-trux-teal">X</span>
            <span className="text-trux-teal text-nano -top-nudge relative">✓</span>
          </span>
          <span className="text-trux-navy text-3xs mt-px font-semibold tracking-widest uppercase">
            Retail Partner
          </span>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-5 py-12">
        <Card className="border-login-border max-w-card w-full rounded-2xl bg-white px-10 pt-11 pb-8 text-center shadow-none">
          <CardHeader className="space-y-0 p-0">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full">
              <img alt="Big Y" className="h-20 w-20 rounded-full object-cover" src={logoUrl} />
            </div>
            <CardTitle className="text-login-ink text-2xl leading-tight font-semibold tracking-tight">
              Big Y
            </CardTitle>
            <p className="text-login-muted mt-1.5 text-sm">AI Merchandising Execution Platform</p>
          </CardHeader>

          <CardContent className="space-y-4 p-0">
            {children}

            <div className="flex items-center justify-center gap-4 pt-1">
              <div className="flex items-center gap-1.5">
                <span className="bg-trust-ok-bg inline-flex h-6 w-6 items-center justify-center rounded-full">
                  <svg aria-hidden="true" fill="none" height="12" viewBox="0 0 10 10" width="12">
                    <path
                      d="M2 5.5L4 7.5L8 3"
                      stroke="#00674B"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                </span>
                <span className="text-login-muted text-xs">Secured</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="bg-trust-info-bg inline-flex h-6 w-6 items-center justify-center rounded-full">
                  <svg aria-hidden="true" fill="none" height="12" viewBox="0 0 10 10" width="12">
                    <rect
                      height="5.5"
                      rx="1"
                      stroke="#185FA5"
                      strokeWidth="1"
                      width="7"
                      x="1.5"
                      y="3"
                    />
                    <path
                      d="M3.5 3V2C3.5 1.4 3.9 1 5 1C6.1 1 6.5 1.4 6.5 2V3"
                      stroke="#185FA5"
                      strokeWidth="1"
                    />
                  </svg>
                </span>
                <span className="text-login-muted text-xs whitespace-nowrap">
                  Dedicated instance
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="bg-trust-shield-bg inline-flex h-6 w-6 items-center justify-center rounded-full">
                  <svg aria-hidden="true" fill="none" height="12" viewBox="0 0 10 10" width="12">
                    <path
                      d="M5 1L8.5 2.5V5C8.5 7.2 5 9 5 9C5 9 1.5 7.2 1.5 5V2.5L5 1Z"
                      stroke="#534AB7"
                      strokeLinejoin="round"
                      strokeWidth="1"
                    />
                    <path
                      d="M3.5 5L4.5 6L6.5 4"
                      stroke="#534AB7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1"
                    />
                  </svg>
                </span>
                <span className="text-login-muted text-xs">SOC2</span>
              </div>
            </div>

            <div className="border-login-divider text-login-muted-2 flex items-center justify-center gap-1 border-t pt-4 text-xs">
              <span>Powered by</span>
              <span className="text-trux-navy tracking-brand-3 font-extrabold">
                TRU<span className="text-trux-teal">X</span>
              </span>
              <span>· Enterprise Retail AI</span>
            </div>
          </CardContent>
        </Card>
      </main>

      <AppFooter />
    </div>
  );
}

export function SessionUnavailableCard({ onRetry }: SessionUnavailableCardProps) {
  return (
    <LoginLayout>
      <div className="space-y-1">
        <p className="text-login-ink text-sm font-semibold">
          We couldn&apos;t restore your session
        </p>
        <p className="text-login-muted text-sm">
          The service is temporarily unavailable. Please try again.
        </p>
      </div>
      <Button className="w-full" onClick={onRetry}>
        Try again
      </Button>
    </LoginLayout>
  );
}
